<template>
  <RecommendationButtonTemplate
    class="button-content"
    :to="to"
    :recommendation="recommendation"
    @click="handleClick"
  >
    <template #icon>
      <img class="media-icon" :src="imgSrc" />
    </template>

    <span class="infos">
      <span class="text title">{{ title }}</span>
      <span class="text sub-title">{{ subTitle }}</span>
      <span class="text level">
        <span class="label">Difficulté</span>
        <span class="value">
          <span class="value-text">{{ level }}</span>
          <icon
            v-if="level > 5 && level < 8"
            class="value-icon"
            size="1.1rem"
            name="nomad:flame"
          />
          <icon
            v-else-if="level >= 8"
            class="value-icon"
            size="1.1rem"
            name="nomad:flamedeath"
          />
        </span>
      </span>
    </span>
  </RecommendationButtonTemplate>
</template>
<script setup lang="ts">
interface Props {
  recommendation: BaseRecommendationSchema;
  mMedia: Map<string, MediaSchema>;
  index: number;
}

const { libraryBookId, categoryManager } = inject(INJECT_CATEGORY_PAGE);

const { category } = categoryManager;

const route = useRoute();

const { chapterAdaptiveRecommendationStarted } =
  useAppEventChapterRecommendation(categoryManager);

const { mScoresForRecommendedItems } = inject(INJECT_RECOMMENDATION);

const props = defineProps({
  recommendation: null,
  mMedia: null,
  index: null
});

const title = computed(() => {
  return `${props.recommendation.title}`;
});

const subTitle = computed(() => {
  return `${props.recommendation.subTitle || ''}`;
});

const level = computed(() => props.recommendation.difficultyLevel);

const imgSrc = computed(() => {
  if (
    !props.recommendation.mediaIconId ||
    !props.mMedia.has(props.recommendation.mediaIconId)
  )
    return null;
  const media = props.mMedia.get(props.recommendation.mediaIconId);

  return media?.urlCompressed || media?.url;
});

const to = computed(() => {
  let { path, query, hash } = route;
  query = { ...query, from: path };

  switch (props.recommendation.type) {
    case 'exercise':
      path = `/${Sections.Revisions}/${props.recommendation.libraryBookSlug}/${props.recommendation.parentSlug}/exercise/${props.recommendation.itemSlug}`;
      break;
    default:
      if (
        props.recommendation.itemsType === 'course' ||
        props.recommendation.itemsType === 'course_video'
      ) {
        path = `/${Sections.Revisions}/${props.recommendation.libraryBookSlug}/${props.recommendation.parentSlug}/post/${props.recommendation.itemSlug}`;
      } else {
        const categorySlug =
          props.recommendation.libraryBookId === libraryBookId
            ? category?.value?.slug
            : props.recommendation.rootSlug;
        path = `/${Sections.Revisions}/${props.recommendation.libraryBookSlug}/${categorySlug}`;
        hash = `#/modal/${props.recommendation.parentSlug}/${props.recommendation.itemSlug}`;
      }
  }
  return {
    path,
    query,
    hash,
  };
});

const handleClick = () => {
  chapterAdaptiveRecommendationStarted(
    props.recommendation.id,
    props.recommendation.type,
    props.index,
    mScoresForRecommendedItems.value.get(props.recommendation.id) || 0
  );
};
</script>
<style lang="scss" scoped>
.button-content {
  width: 22.4rem;
}

.infos {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  gap: var(--gap_xxs);
  justify-items: flex-start;
  .text {
    max-width: stretch;
    overflow: hidden;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .title {
    @include font('n', 600, 'secondary');
  }
  .sub-title {
    @include font('xs', 400, 'secondary');
  }
  .level {
    overflow: visible;
    @include font('xxs', 600, 'secondary');
  }
}

.value {
  display: inline-block;
  position: relative;
  padding-left: var(--gap_xs);
  &-text {
    display: inline-block;
    padding: 0.2rem 0.5rem;
    border-radius: var(--stroke_radius_s);
    background-color: var(--color_secondary_lighter);
    color: var(--color_secondary_darker);
  }
  &-icon {
    position: absolute;
    z-index: 1;
    top: -0.4rem;
    right: -0.65rem;
  }
}

.media-icon {
  width: 3.5rem;
  height: auto;
}
</style>
